import { getApiConfig } from "./config.api";

interface ApiResponse<T = any> {
    data: T;
    token?: string ;
    message?: string;
    error?: string;
    state: boolean;
}

interface RequestConfig extends RequestInit {
    skipAuth?: boolean;  
    skipErrorRedirect?: boolean;
    timeout?: number; // Nuevo parámetro opcional para timeout personalizado
}

class ApiClient {
    private baseURL: string;
    private defaultTimeout: number = 20000; // 20 segundos por defecto

    constructor() {
        this.baseURL = getApiConfig().BASE_URL; 
    }

    async request<T = any>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
        const { skipAuth = false, skipErrorRedirect = false, timeout = this.defaultTimeout, ...requestConfig } = config;
        const url = `${this.baseURL}${endpoint}`;
        const token = localStorage.getItem('token');
        const isFormData = requestConfig.body instanceof FormData;
        const headers: Record<string, string> = {
            ...(requestConfig.headers as Record<string, string>),
        };

        if (!isFormData) {
            headers['Content-Type'] = 'application/json';
        }

        if (!skipAuth && token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const finalConfig: RequestInit = {
            ...requestConfig,
            headers,
        };

        // Crear AbortController para el timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        finalConfig.signal = controller.signal;

        try {
            const response = await fetch(url, finalConfig);

            // Limpiar el timeout si la petición se completó exitosamente
            clearTimeout(timeoutId);

            // Manejar error de autenticación (401)
            if (response.status === 401 && !skipAuth) {
                this.handleAuthError();
                throw new Error('Sesión expirada');
            }

            // Manejar error del servidor (500)
            if (response.status === 500 && !skipErrorRedirect) {
                this.handleServerError(response);
                throw new Error('Error interno del servidor');
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Error ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            // Limpiar el timeout en caso de error
            clearTimeout(timeoutId);
            
            if (error instanceof Error) {
                // Verificar si el error es por timeout
                if (error.name === 'AbortError') {
                    throw new Error(`Timeout: La petición tardó más de ${timeout}ms`);
                }
                throw error;
            }
            throw new Error('Error de conexión');
        }
    }

    private handleAuthError() {
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Redirigir al login si no estamos ya ahí
        if (window.location.pathname !== '/') {
            window.location.href = '/';
        }
    }

    private handleServerError(response: Response) {

        // Redirigir a página de error si no estamos ya ahí
        if (window.location.pathname !== '/error') {
            // Opcionalmente puedes pasar información del error como query params
            // const errorInfo = new URLSearchParams({
            //     status: response.status.toString(),
            //     url: response.url,
            //     timestamp: new Date().toISOString()
            // });
            console.log(response);
            
            
            window.location.href = `/error`;
        }
    }

    // Métodos de conveniencia
    async get<T = any>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...config, method: 'GET' });
    }

    async post<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async put<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async patch<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T = any>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...config, method: 'DELETE' });
    }

    // Métodos específicos para archivos
    async uploadFile<T = any>(
    endpoint: string, 
    file: File, 
    additionalData?: Record<string, any>, 
    config?: RequestConfig & { timeout?: number }
): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
        Object.entries(additionalData).forEach(([key, value]) => {
            formData.append(key, value);
        });
    }
    
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {};
    
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    
    // Configurar timeout (por defecto 30 segundos, pero configurable)
    const timeoutMs = config?.timeout || 40000;
    
    return this.request<T>(endpoint, {
        ...config,
        method: 'POST',
        body: formData,
        headers,
        timeout: timeoutMs, // Pasar el timeout al request
    });
}

    // Método utilitario para realizar peticiones sin redirección automática en errores 500
    async requestWithoutErrorRedirect<T = any>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...config, skipErrorRedirect: true });
    }

    // Método utilitario para cambiar el timeout por defecto
    setDefaultTimeout(timeout: number): void {
        this.defaultTimeout = timeout;
    }
}

// Instancia singleton
export const apiClient = new ApiClient();